import * as React from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Avatar,
  AvatarGroup,
  Box,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import ChannelStatusMenu from "./ChannelStatusMenu";
import LinkAbrirDetalhes from "./LinkAbrirDetalhes";
import AssociateMenu from "./AssociateMenu";
import { useUserStore } from "../store/userStore";
import {
  associateProcessByEmail,
  desassociateProcessByEmail,
  getUserByEmail,
} from "../services/clientService";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";

interface ProcessTablesProps {
  list: any;
  loadMoreData: () => void;
  reloadData: () => void;
  isLoading: boolean;
  height?: string;
  backgroundColor?: string;
  textColorChange?: boolean;
}

const ProcessPerClientTable: React.FC<ProcessTablesProps> = ({
  list,
  height = "71vh",
  backgroundColor = "#326AFD",
  textColorChange = false,
  isLoading,
  reloadData,
  loadMoreData,
}) => {
  const currentUserEmail = useUserStore((state) => state.email);

  const [userInfos, setUserInfos] = useState<Record<string, any>>({});
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: backgroundColor,
      color: textColorChange ? "#0A0C33" : theme.palette.common.white,
      fontWeight: "bold",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      padding: "6px 8px",
      height: "58px",
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  const fetchUserInfo = async (email: string) => {
    try {
      if (userInfos[email]) return;

      const data = await getUserByEmail(email);
      setUserInfos((prev) => ({
        ...prev,
        [email]: data,
      }));
    } catch (error) {
      console.error("Erro ao buscar dados do usuário:", error);
    }
  };

  useEffect(() => {
    const allEmails = list.flatMap((item: any) => item.analistas ?? []);

    allEmails.forEach((email: any) => {
      if (email) fetchUserInfo(email);
    });
  }, [list]);
  const token = useUserStore((state) => state.idToken);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const { scrollHeight, clientHeight } = container;
      if (scrollHeight <= clientHeight && list.length > 0 && !isLoading) {
        loadMoreData();
      }
    }
  }, [list, isLoading]);

  const handleAssociar = async (codigo: string, isCurrentlyLinked: boolean) => {
    const email = currentUserEmail;

    try {
      if (!isCurrentlyLinked) {
        await associateProcessByEmail(codigo, email, token);
        toast.success("Processo associado com sucesso!");
      } else {
        await desassociateProcessByEmail(codigo, email);
        toast.success("Processo desassociado com sucesso!");
      }

      reloadData();
    } catch (error: any) {
      console.error("Erro:", error.response?.data || error.message);
      toast.error("Erro ao atualizar associação.");
    }
  };

  const handleScroll = () => {
    if (tableContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        tableContainerRef.current;
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        loadMoreData();
      }
    }
  };

  return (
    <>
      {!isLoading && list.length === 0 && (
        <Box textAlign="left" color="#666" fontSize="1rem">
          Nenhum processo encontrado.
        </Box>
      )}

      {list.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "17px",
            height: height,
          }}
          ref={tableContainerRef}
          onScroll={handleScroll}
        >
          <Table sx={{ minWidth: 700 }} aria-label="customized table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell align="center"></StyledTableCell>
                <StyledTableCell align="center">REF</StyledTableCell>
                <StyledTableCell align="center">Nº DI</StyledTableCell>
                <StyledTableCell align="center">B/L</StyledTableCell>
                <StyledTableCell align="center">CONTAINER</StyledTableCell>
                <StyledTableCell align="center">CANAL</StyledTableCell>
                <StyledTableCell align="center">STATUS</StyledTableCell>
                <StyledTableCell align="center">EQUIPE</StyledTableCell>
                <StyledTableCell align="center">DETALHES</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {list.map((row: any) => {
                const userEmails = row.analistas ?? [];
                const hasEmails = userEmails.length > 0;
                const isUserLinked = userEmails.includes(currentUserEmail);

                return (
                  <StyledTableRow key={row.CD_PROCESSO}>
                    <StyledTableCell component="th" scope="row" align="center">
                      <AssociateMenu
                        checked={isUserLinked}
                        onClick={() =>
                          handleAssociar(row.CD_PROCESSO, isUserLinked)
                        }
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.CD_PROCESSO}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.NR_DI ?? "--"}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.NR_DOC_CARGA ?? "--"}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.NR_CONTAINER && row.NR_CONTAINER.length > 0
                        ? row.NR_CONTAINER[0]
                        : "--"}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <ChannelStatusMenu status={row.CANAL} />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.HISTORICO_RESUMO?.INF_HISTORICO ?? "--"}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {hasEmails && (
                        <AvatarGroup
                          max={4}
                          sx={{
                            justifyContent: "center",
                            "& .MuiAvatar-root": {
                              width: 24,
                              height: 24,
                              fontSize: 12,
                            },
                          }}
                        >
                          {userEmails.map((email: string) => {
                            const info = userInfos[email];
                            const [firstName = "", lastName = ""] =
                              info?.name?.trim()?.split(" ") ?? [];
                            const tooltipTitle = info?.name;

                            return (
                              <Tooltip
                                key={email}
                                title={tooltipTitle}
                                arrow
                                style={{ alignItems: "center" }}
                                PopperProps={{
                                  modifiers: [
                                    {
                                      name: "zIndex",
                                      enabled: true,
                                      options: { zIndex: 9999 },
                                    },
                                  ],
                                }}
                              >
                                <Avatar
                                  src={info?.picture || undefined}
                                  alt={info?.name || email}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    fontSize: "0.75rem",
                                    backgroundColor: info?.picture
                                      ? "transparent"
                                      : "#326AFD",
                                    color: "#fff",
                                  }}
                                >
                                  {!info?.picture &&
                                    `${firstName[0] || ""}${lastName[0] || ""}`}
                                </Avatar>
                              </Tooltip>
                            );
                          })}
                        </AvatarGroup>
                      )}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <LinkAbrirDetalhes item={row} />
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
          {isLoading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "10px",
              }}
            >
              <Box display="flex" justifyContent="center" alignItems="center">
                <CircularProgress
                  size={48}
                  thickness={4}
                  style={{ color: "#326AFD" }}
                />
              </Box>
            </div>
          )}
        </TableContainer>
      )}
    </>
  );
};

export default ProcessPerClientTable;
